import React, { useEffect, useState, useCallback } from "react";
import api from "../api/axios";
import {
  FaCheckCircle, FaTimesCircle, FaFilter, FaSearch,
  FaSyncAlt, FaExclamationTriangle, FaBan, FaMoneyCheckAlt,
  FaArrowDown, FaArrowUp, FaCalendarAlt, FaRegClock,
  FaUndo, FaCheckSquare, FaSquare, FaInfoCircle,
} from "react-icons/fa";

// ─── Constants ────────────────────────────────────────────────────────────────
const CHEQUE_STATUSES = ["Pending", "Cleared", "Bounced", "Cancelled"];

const STATUS_STYLES = {
  Pending:   { bg: "#fff8e1", color: "#b45309", border: "#fcd34d", icon: <FaRegClock /> },
  Cleared:   { bg: "#d1fae5", color: "#065f46", border: "#6ee7b7", icon: <FaCheckCircle /> },
  Bounced:   { bg: "#fee2e2", color: "#991b1b", border: "#fca5a5", icon: <FaTimesCircle /> },
  Cancelled: { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db", icon: <FaBan /> },
};

const TABS = [
  { key: "receipt", label: "PDC Receipts",  icon: <FaArrowDown />, color: "#0077cc", gradient: "linear-gradient(135deg,#0077cc,#00aaff)" },
  { key: "payment", label: "PDC Payments",  icon: <FaArrowUp />,   color: "#7c3aed", gradient: "linear-gradient(135deg,#7c3aed,#9333ea)" },
];

const RECON_FILTERS = [
  { value: "all",   label: "All" },
  { value: "false", label: "Unreconciled" },
  { value: "true",  label: "Reconciled" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtCurrency = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "AED" }).format(v || 0);

const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-GB", {
  day: "2-digit", month: "short", year: "numeric"
}) : "—");

// ─── Sub-components ───────────────────────────────────────────────────────────
const DueBadge = ({ days, status }) => {
  if (status !== "Pending" || days === null || days === undefined) return null;
  if (days < 0)
    return (
      <span style={{ color: "#dc2626", fontWeight: 700, fontSize: "0.75rem" }}>
        <FaExclamationTriangle style={{ marginRight: 2 }} />
        {Math.abs(days)}d overdue
      </span>
    );
  if (days === 0)
    return <span style={{ color: "#ea580c", fontWeight: 700, fontSize: "0.75rem" }}>Due today</span>;
  if (days <= 7)
    return <span style={{ color: "#d97706", fontWeight: 600, fontSize: "0.75rem" }}>Due in {days}d</span>;
  return <span style={{ color: "#6b7280", fontSize: "0.75rem" }}>{days}d left</span>;
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.Pending;
  return (
    <span style={{
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      borderRadius: 20, padding: "3px 11px", fontSize: "0.76rem", fontWeight: 700,
      display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap",
    }}>
      {s.icon} {status}
    </span>
  );
};

const ReconBadge = ({ isReconciled, reconciledAt }) => {
  if (isReconciled) {
    return (
      <span title={reconciledAt ? `Reconciled at: ${reconciledAt}` : "Reconciled"} style={{
        background: "#d1fae5", color: "#065f46", border: "1px solid #6ee7b7",
        borderRadius: 20, padding: "2px 10px", fontSize: "0.72rem", fontWeight: 700,
        display: "inline-flex", alignItems: "center", gap: 3, whiteSpace: "nowrap",
      }}>
        <FaCheckCircle style={{ fontSize: 10 }} /> Reconciled
      </span>
    );
  }
  return (
    <span style={{
      background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d",
      borderRadius: 20, padding: "2px 10px", fontSize: "0.72rem", fontWeight: 700,
      display: "inline-flex", alignItems: "center", gap: 3, whiteSpace: "nowrap",
    }}>
      <FaRegClock style={{ fontSize: 10 }} /> Pending
    </span>
  );
};

// ─── Status Dropdown Button ───────────────────────────────────────────────────
const StatusChanger = ({ cheque, onStatusChange, working }) => {
  const [open, setOpen] = useState(false);
  const options = CHEQUE_STATUSES.filter((s) => s !== cheque.cheque_status);

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        disabled={working}
        onClick={() => setOpen((o) => !o)}
        style={{
          border: "1px solid #d1d5db", borderRadius: 8, padding: "5px 12px",
          background: "#fff", color: "#374151", fontWeight: 600, fontSize: "0.8rem",
          cursor: working ? "wait" : "pointer", display: "flex", alignItems: "center",
          gap: 5, whiteSpace: "nowrap",
        }}
      >
        {working ? "Updating…" : "Status ▾"}
      </button>
      {open && (
        <div style={{
          position: "absolute", right: 0, top: "110%", background: "#fff",
          border: "1px solid #e5e7eb", borderRadius: 10,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 999,
          minWidth: 160, overflow: "hidden",
        }}>
          {options.map((s) => {
            const style = STATUS_STYLES[s];
            return (
              <button
                key={s}
                onClick={() => { setOpen(false); onStatusChange(cheque, s); }}
                style={{
                  display: "flex", alignItems: "center", gap: 8, width: "100%",
                  padding: "9px 14px", border: "none", background: "transparent",
                  cursor: "pointer", fontSize: "0.85rem", fontWeight: 600,
                  color: style.color, textAlign: "left", transition: "background 0.15s",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = style.bg}
                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
              >
                {style.icon} Mark as {s}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Summary Card ─────────────────────────────────────────────────────────────
const SummaryCard = ({ label, value, sub, color, icon }) => (
  <div style={{
    background: "#fff", borderRadius: 14, padding: "18px 20px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)", borderTop: `4px solid ${color}`,
    minWidth: 130, flex: 1,
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
      <span style={{ color, fontSize: 14 }}>{icon}</span>
      <span style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>
        {label}
      </span>
    </div>
    <div style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>{sub}</div>}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
export default function ChequeManager() {
  const [activeTab, setActiveTab] = useState("receipt");

  // Data
  const [cheques, setCheques] = useState({ receipt: [], payment: [] });
  const [summaries, setSummaries] = useState({ receipt: null, payment: null });
  const [loading, setLoading] = useState({ receipt: false, payment: false });
  const [error, setError] = useState({ receipt: null, payment: null });
  const [working, setWorking] = useState({});   // { "receipt-5": true }
  const [selected, setSelected] = useState({}); // { "receipt": Set<id> }
  const [bulkWorking, setBulkWorking] = useState(false);
  const [toast, setToast] = useState(null);

  // Filters (per tab)
  const [filters, setFilters] = useState({
    receipt: { status: "all", reconStatus: "all", startDate: "", endDate: "", search: "" },
    payment: { status: "all", reconStatus: "all", startDate: "", endDate: "", search: "" },
  });

  // ── Toast helper ─────────────────────────────────────────────────────────
  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Load cheques for one tab ───────────────────────────────────────────────
  const loadCheques = useCallback(async (tab) => {
    const f = filters[tab];
    setLoading((l) => ({ ...l, [tab]: true }));
    setError((e) => ({ ...e, [tab]: null }));
    try {
      const params = new URLSearchParams({ cheque_type: tab });
      if (f.status !== "all") params.append("cheque_status", f.status);
      if (f.reconStatus !== "all") params.append("is_reconciled", f.reconStatus);
      if (f.startDate) params.append("start_date", f.startDate);
      if (f.endDate) params.append("end_date", f.endDate);

      const res = await api.get(`cheque-manager/cheques?${params}`);
      const items = res.data.cheques || [];
      setCheques((c) => ({ ...c, [tab]: items }));

      // Use per-type summary from response
      const sumKey = tab === "receipt" ? "receipt_summary" : "payment_summary";
      setSummaries((s) => ({ ...s, [tab]: res.data[sumKey] || null }));

      // Clear selection on refresh
      setSelected((sel) => ({ ...sel, [tab]: new Set() }));
    } catch (e) {
      setError((er) => ({ ...er, [tab]: "Failed to load cheques." }));
      console.error(e);
    } finally {
      setLoading((l) => ({ ...l, [tab]: false }));
    }
  }, [filters]);

  useEffect(() => { loadCheques("receipt"); }, []); // eslint-disable-line

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (cheques[tab].length === 0 && !loading[tab]) {
      loadCheques(tab);
    }
  };

  // ── Status change ─────────────────────────────────────────────────────────
  const handleStatusChange = async (cheque, newStatus) => {
    const key = `${cheque.txn_type}-${cheque.id}`;
    setWorking((w) => ({ ...w, [key]: true }));
    try {
      const res = await api.patch(`cheque-manager/status/${cheque.txn_type}/${cheque.id}?new_status=${encodeURIComponent(newStatus)}`);
      const updated = res.data.data;
      setCheques((prev) => ({
        ...prev,
        [cheque.txn_type]: prev[cheque.txn_type].map((c) =>
          c.id === cheque.id ? updated : c
        ),
      }));
      showToast(`Cheque status updated to "${newStatus}"`);
    } catch (e) {
      console.error(e);
      showToast("Failed to update cheque status.", "error");
    } finally {
      setWorking((w) => ({ ...w, [key]: false }));
    }
  };

  // ── Reconcile single ─────────────────────────────────────────────────────
  const handleReconcile = async (cheque) => {
    const key = `${cheque.txn_type}-${cheque.id}`;
    setWorking((w) => ({ ...w, [key]: true }));
    try {
      const res = await api.patch(`cheque-manager/reconcile/${cheque.txn_type}/${cheque.id}`);
      const updated = res.data.data;
      setCheques((prev) => ({
        ...prev,
        [cheque.txn_type]: prev[cheque.txn_type].map((c) => c.id === cheque.id ? updated : c),
      }));
      showToast("Cheque marked as reconciled ✓");
    } catch (e) {
      console.error(e);
      showToast("Failed to reconcile cheque.", "error");
    } finally {
      setWorking((w) => ({ ...w, [key]: false }));
    }
  };

  // ── Un-reconcile single ───────────────────────────────────────────────────
  const handleUnreconcile = async (cheque) => {
    const key = `${cheque.txn_type}-${cheque.id}`;
    setWorking((w) => ({ ...w, [key]: true }));
    try {
      const res = await api.patch(`cheque-manager/unreconcile/${cheque.txn_type}/${cheque.id}`);
      const updated = res.data.data;
      setCheques((prev) => ({
        ...prev,
        [cheque.txn_type]: prev[cheque.txn_type].map((c) => c.id === cheque.id ? updated : c),
      }));
      showToast("Cheque reconciliation reversed.");
    } catch (e) {
      console.error(e);
      showToast("Failed to un-reconcile cheque.", "error");
    } finally {
      setWorking((w) => ({ ...w, [key]: false }));
    }
  };

  // ── Bulk reconcile ────────────────────────────────────────────────────────
  const handleBulkReconcile = async (tab) => {
    const ids = selected[tab] ? [...selected[tab]] : [];
    if (ids.length === 0) return;
    setBulkWorking(true);
    try {
      const items = ids.map((id) => ({ txn_type: tab, id }));
      await api.post(`cheque-manager/bulk-reconcile`, { items });
      showToast(`${ids.length} cheque(s) reconciled successfully ✓`);
      loadCheques(tab);
    } catch (e) {
      console.error(e);
      showToast("Bulk reconcile failed.", "error");
    } finally {
      setBulkWorking(false);
    }
  };

  // ── Selection helpers ──────────────────────────────────────────────────────
  const toggleSelect = (tab, id) => {
    setSelected((prev) => {
      const set = new Set(prev[tab] || []);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...prev, [tab]: set };
    });
  };

  const toggleSelectAll = (tab, rows) => {
    setSelected((prev) => {
      const set = prev[tab] || new Set();
      const allIds = rows.map((r) => r.id);
      const allSelected = allIds.every((id) => set.has(id));
      if (allSelected) return { ...prev, [tab]: new Set() };
      return { ...prev, [tab]: new Set(allIds) };
    });
  };

  // ── Filter helpers ────────────────────────────────────────────────────────
  const setFilter = (tab, key, val) => {
    setFilters((f) => ({ ...f, [tab]: { ...f[tab], [key]: val } }));
  };

  const resetFilters = (tab) => {
    setFilters((f) => ({
      ...f,
      [tab]: { status: "all", reconStatus: "all", startDate: "", endDate: "", search: "" },
    }));
  };

  const displayCheques = (tab) => {
    const f = filters[tab];
    const q = f.search.toLowerCase();
    return cheques[tab].filter((c) => {
      if (!q) return true;
      return (
        (c.voucher_no || "").toLowerCase().includes(q) ||
        (c.party || "").toLowerCase().includes(q) ||
        (c.cheque_number || "").toLowerCase().includes(q) ||
        (c.bank_name || "").toLowerCase().includes(q)
      );
    });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Styles
  // ─────────────────────────────────────────────────────────────────────────
  const S = {
    page: {
      padding: "28px 32px",
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
      background: "linear-gradient(135deg, #f0f4ff 0%, #f8faff 100%)",
      minHeight: "100vh",
    },
    header: {
      display: "flex", alignItems: "center", gap: 18, marginBottom: 28,
      background: "#fff", borderRadius: 16, padding: "20px 28px",
      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    },
    iconBox: {
      background: "linear-gradient(135deg, #0077cc, #7c3aed)",
      borderRadius: 16, padding: "14px 18px", color: "#fff", fontSize: 26,
      boxShadow: "0 6px 18px rgba(99,102,241,0.28)", flexShrink: 0,
    },
    title: { margin: 0, fontWeight: 800, fontSize: "1.55rem", color: "#111827" },
    subtitle: { margin: "3px 0 0", color: "#6b7280", fontSize: "0.88rem" },

    // Tabs
    tabBar: {
      display: "flex", gap: 0, background: "#fff",
      borderRadius: 14, padding: 6, marginBottom: 24,
      boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
      width: "fit-content",
    },
    tab: (active, tab) => ({
      padding: "10px 28px", border: "none",
      borderRadius: 10,
      background: active ? tab.gradient : "transparent",
      fontWeight: active ? 700 : 500,
      color: active ? "#fff" : "#6b7280",
      cursor: "pointer", fontSize: "0.92rem",
      display: "flex", alignItems: "center", gap: 8,
      transition: "all 0.2s",
      boxShadow: active ? "0 4px 12px rgba(0,0,0,0.15)" : "none",
    }),

    // Summary row
    summaryRow: {
      display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 24,
    },

    // Filter bar
    filterBar: {
      display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end",
      background: "#fff", borderRadius: 14,
      padding: "18px 22px", marginBottom: 20,
      boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
      border: "1px solid #e5e7eb",
    },
    label: {
      fontSize: 10, color: "#6b7280", display: "block", marginBottom: 5,
      fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
    },
    select: {
      padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db",
      fontSize: 13, color: "#374151", background: "#f9fafb",
      outline: "none", cursor: "pointer",
    },
    input: {
      padding: "8px 10px", borderRadius: 8, border: "1px solid #d1d5db",
      fontSize: 13, color: "#374151", background: "#f9fafb", outline: "none",
    },
    applyBtn: (color) => ({
      background: `linear-gradient(135deg, ${color}, ${color}cc)`,
      color: "#fff", border: "none", borderRadius: 8,
      padding: "9px 22px", fontWeight: 700, cursor: "pointer",
      fontSize: "0.88rem", display: "flex", alignItems: "center", gap: 6,
      boxShadow: `0 4px 10px ${color}40`,
      transition: "transform 0.15s",
    }),
    resetBtn: {
      background: "#f3f4f6", color: "#6b7280", border: "1px solid #e5e7eb",
      borderRadius: 8, padding: "9px 16px", fontWeight: 600,
      cursor: "pointer", fontSize: "0.88rem",
    },

    // Bulk bar
    bulkBar: (color) => ({
      display: "flex", alignItems: "center", gap: 12, marginBottom: 12,
      background: `${color}12`, border: `1px solid ${color}30`,
      borderRadius: 10, padding: "10px 18px",
    }),
    bulkBtn: (color) => ({
      background: color, color: "#fff", border: "none",
      borderRadius: 8, padding: "7px 18px", fontWeight: 700,
      cursor: "pointer", fontSize: "0.85rem", display: "flex",
      alignItems: "center", gap: 6,
    }),

    // Table
    tableWrap: {
      overflowX: "auto", borderRadius: 14,
      boxShadow: "0 2px 12px rgba(0,0,0,0.07)", background: "#fff",
    },
    th: {
      padding: "13px 16px", textAlign: "left", fontWeight: 700,
      fontSize: "0.78rem", color: "#6366f1", background: "#f5f3ff",
      textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap",
    },
    td: {
      padding: "12px 16px", fontSize: "0.86rem", color: "#374151", verticalAlign: "middle",
    },
    actionBtn: (color, bg) => ({
      background: bg, color, border: `1px solid ${color}40`,
      borderRadius: 7, padding: "5px 12px", fontWeight: 700,
      cursor: "pointer", fontSize: "0.78rem", display: "inline-flex",
      alignItems: "center", gap: 5, whiteSpace: "nowrap", transition: "opacity 0.15s",
    }),
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render tab content
  // ─────────────────────────────────────────────────────────────────────────
  const renderTab = (tab) => {
    const f = filters[tab.key];
    const rows = displayCheques(tab.key);
    const summary = summaries[tab.key];
    const sel = selected[tab.key] || new Set();
    const selCount = sel.size;
    const allSelected = rows.length > 0 && rows.every((r) => sel.has(r.id));

    const summaryCards = summary ? [
      { label: "Total",         value: summary.total,             color: tab.color, icon: <FaMoneyCheckAlt />,    sub: null },
      { label: "Pending",       value: summary.pending_count,     color: "#d97706", icon: <FaRegClock />,         sub: fmtCurrency(summary.pending_amount) },
      { label: "Overdue",       value: summary.overdue_count,     color: "#dc2626", icon: <FaExclamationTriangle />, sub: "Still pending" },
      { label: "Cleared",       value: summary.cleared_count,     color: "#059669", icon: <FaCheckCircle />,      sub: fmtCurrency(summary.cleared_amount) },
      { label: "Bounced",       value: summary.bounced_count,     color: "#ef4444", icon: <FaTimesCircle />,      sub: fmtCurrency(summary.bounced_amount) },
      { label: "Reconciled",    value: summary.reconciled_count,  color: "#0891b2", icon: <FaCheckSquare />,      sub: fmtCurrency(summary.reconciled_amount) },
      { label: "Unreconciled",  value: summary.unreconciled_count, color: "#7c3aed", icon: <FaInfoCircle />,     sub: fmtCurrency(summary.unreconciled_amount) },
    ] : [];

    return (
      <div>
        {/* Summary Cards */}
        {summary && (
          <div style={S.summaryRow}>
            {summaryCards.map((card) => (
              <SummaryCard key={card.label} {...card} />
            ))}
          </div>
        )}

        {/* Filter Bar */}
        <div style={S.filterBar}>
          {/* Cheque Status */}
          <div>
            <label style={S.label}><FaFilter style={{ marginRight: 3 }} />Cheque Status</label>
            <select
              id={`filter-status-${tab.key}`}
              style={S.select}
              value={f.status}
              onChange={(e) => setFilter(tab.key, "status", e.target.value)}
            >
              <option value="all">All Statuses</option>
              {CHEQUE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Reconciliation Status */}
          <div>
            <label style={S.label}><FaCheckCircle style={{ marginRight: 3 }} />Reconciliation</label>
            <select
              id={`filter-recon-${tab.key}`}
              style={S.select}
              value={f.reconStatus}
              onChange={(e) => setFilter(tab.key, "reconStatus", e.target.value)}
            >
              {RECON_FILTERS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          {/* Cheque Date From */}
          <div>
            <label style={S.label}><FaCalendarAlt style={{ marginRight: 3 }} />Cheque Date From</label>
            <input
              id={`filter-start-${tab.key}`}
              type="date"
              style={S.input}
              value={f.startDate}
              onChange={(e) => setFilter(tab.key, "startDate", e.target.value)}
            />
          </div>

          {/* Cheque Date To */}
          <div>
            <label style={S.label}>Cheque Date To</label>
            <input
              id={`filter-end-${tab.key}`}
              type="date"
              style={S.input}
              value={f.endDate}
              onChange={(e) => setFilter(tab.key, "endDate", e.target.value)}
            />
          </div>

          {/* Search */}
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={S.label}><FaSearch style={{ marginRight: 3 }} />Search</label>
            <input
              id={`filter-search-${tab.key}`}
              type="text"
              placeholder="Party, cheque no, bank…"
              style={{ ...S.input, width: "100%", boxSizing: "border-box" }}
              value={f.search}
              onChange={(e) => setFilter(tab.key, "search", e.target.value)}
            />
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <button
              id={`filter-apply-${tab.key}`}
              style={S.applyBtn(tab.color)}
              onClick={() => loadCheques(tab.key)}
            >
              <FaSyncAlt /> Apply
            </button>
            <button
              id={`filter-reset-${tab.key}`}
              style={S.resetBtn}
              onClick={() => { resetFilters(tab.key); setTimeout(() => loadCheques(tab.key), 0); }}
            >
              Reset
            </button>
          </div>
        </div>

        {/* Bulk bar */}
        {selCount > 0 && (
          <div style={S.bulkBar(tab.color)}>
            <span style={{ fontWeight: 700, color: tab.color, fontSize: "0.88rem" }}>
              <FaCheckSquare style={{ marginRight: 6 }} />
              {selCount} cheque(s) selected
            </span>
            <button
              id={`bulk-reconcile-${tab.key}`}
              style={S.bulkBtn("#059669")}
              onClick={() => handleBulkReconcile(tab.key)}
              disabled={bulkWorking}
            >
              <FaCheckCircle /> {bulkWorking ? "Processing…" : "Reconcile Selected"}
            </button>
            <button
              style={{ ...S.bulkBtn("#6b7280"), background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db" }}
              onClick={() => setSelected((s) => ({ ...s, [tab.key]: new Set() }))}
            >
              Clear Selection
            </button>
          </div>
        )}

        {/* Error */}
        {error[tab.key] && (
          <div style={{ background: "#fee2e2", color: "#991b1b", borderRadius: 8, padding: "10px 16px", marginBottom: 14 }}>
            {error[tab.key]}
          </div>
        )}

        {/* Loading */}
        {loading[tab.key] ? (
          <div style={{ textAlign: "center", padding: 60, background: "#fff", borderRadius: 14 }}>
            <div style={{
              width: 40, height: 40, border: `4px solid ${tab.color}30`,
              borderTop: `4px solid ${tab.color}`, borderRadius: "50%",
              animation: "spin 0.8s linear infinite", margin: "0 auto 16px",
            }} />
            <p style={{ color: "#6b7280", margin: 0 }}>Loading cheques…</p>
          </div>
        ) : (
          <div style={S.tableWrap}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #ede9fe" }}>
                  {/* Select all */}
                  <th style={{ ...S.th, width: 40, textAlign: "center" }}>
                    <button
                      onClick={() => toggleSelectAll(tab.key, rows)}
                      style={{ border: "none", background: "transparent", cursor: "pointer", color: tab.color, fontSize: 16 }}
                      title={allSelected ? "Deselect all" : "Select all"}
                    >
                      {allSelected ? <FaCheckSquare /> : <FaSquare />}
                    </button>
                  </th>
                  {[
                    "Voucher No", "Txn Date", "Cheque No", "Cheque Date",
                    "Due / Overdue", "Party", "Bank", "Amount",
                    "Cheque Status", "Reconciled", "Actions"
                  ].map((h) => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={12} style={{ textAlign: "center", padding: 50, color: "#9ca3af", fontStyle: "italic" }}>
                      No cheques found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  rows.map((c, i) => {
                    const wkey = `${c.txn_type}-${c.id}`;
                    const isOverdue = c.is_overdue && c.cheque_status === "Pending";
                    const isSelected = sel.has(c.id);

                    return (
                      <tr
                        key={wkey}
                        style={{
                          borderBottom: "1px solid #f3f4f6",
                          background: isSelected
                            ? `${tab.color}10`
                            : isOverdue
                              ? "#fff7f7"
                              : i % 2 === 0 ? "#fff" : "#fafbff",
                          transition: "background 0.15s",
                        }}
                      >
                        {/* Checkbox */}
                        <td style={{ ...S.td, textAlign: "center" }}>
                          <button
                            onClick={() => toggleSelect(tab.key, c.id)}
                            style={{ border: "none", background: "transparent", cursor: "pointer", color: isSelected ? tab.color : "#d1d5db", fontSize: 16 }}
                          >
                            {isSelected ? <FaCheckSquare /> : <FaSquare />}
                          </button>
                        </td>

                        {/* Voucher No */}
                        <td style={{ ...S.td, fontWeight: 700, color: tab.color }}>
                          {c.voucher_no}
                        </td>

                        {/* Transaction Date */}
                        <td style={S.td}>{fmtDate(c.transaction_date)}</td>

                        {/* Cheque No */}
                        <td style={{ ...S.td, fontFamily: "monospace", fontWeight: 600 }}>
                          {c.cheque_number || "—"}
                        </td>

                        {/* Cheque Date */}
                        <td style={S.td}>
                          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <FaCalendarAlt style={{ color: "#9ca3af", fontSize: 11 }} />
                            {fmtDate(c.cheque_date)}
                          </span>
                        </td>

                        {/* Days Until Due */}
                        <td style={S.td}>
                          {c.cheque_date ? (
                            <DueBadge days={c.days_until_due} status={c.cheque_status} />
                          ) : "—"}
                        </td>

                        {/* Party */}
                        <td style={S.td}>
                          <div style={{ fontWeight: 600 }}>{c.party || "—"}</div>
                          {c.party_type && (
                            <div style={{ fontSize: "0.72rem", color: "#9ca3af" }}>{c.party_type}</div>
                          )}
                        </td>

                        {/* Bank */}
                        <td style={S.td}>
                          {c.bank_name ? (
                            <span style={{ fontSize: "0.82rem" }}>🏦 {c.bank_name}</span>
                          ) : "—"}
                        </td>

                        {/* Amount */}
                        <td style={{ ...S.td, fontWeight: 800, color: tab.key === "receipt" ? "#0077cc" : "#7c3aed" }}>
                          {fmtCurrency(c.amount)}
                        </td>

                        {/* Cheque Status */}
                        <td style={S.td}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <StatusBadge status={c.cheque_status} />
                            {isOverdue && (
                              <span style={{ fontSize: "0.7rem", color: "#dc2626", fontWeight: 600 }}>
                                ⚠ Overdue
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Reconciliation Status */}
                        <td style={S.td}>
                          <ReconBadge isReconciled={c.is_reconciled} reconciledAt={c.reconciled_at} />
                        </td>

                        {/* Actions */}
                        <td style={{ ...S.td, whiteSpace: "nowrap" }}>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {/* Change cheque status */}
                            <StatusChanger
                              cheque={c}
                              onStatusChange={handleStatusChange}
                              working={!!working[wkey]}
                            />

                            {/* Reconcile / Un-reconcile */}
                            {c.is_reconciled ? (
                              <button
                                id={`unreconcile-${tab.key}-${c.id}`}
                                disabled={!!working[wkey]}
                                onClick={() => handleUnreconcile(c)}
                                style={S.actionBtn("#6b7280", "#f3f4f6")}
                                title="Reverse reconciliation"
                              >
                                <FaUndo style={{ fontSize: 11 }} />
                                {working[wkey] ? "…" : "Un-reconcile"}
                              </button>
                            ) : (
                              <button
                                id={`reconcile-${tab.key}-${c.id}`}
                                disabled={!!working[wkey]}
                                onClick={() => handleReconcile(c)}
                                style={S.actionBtn("#059669", "#d1fae5")}
                                title="Mark as reconciled"
                              >
                                <FaCheckCircle style={{ fontSize: 11 }} />
                                {working[wkey] ? "…" : "Reconcile"}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Row count */}
        {!loading[tab.key] && rows.length > 0 && (
          <div style={{ marginTop: 10, color: "#9ca3af", fontSize: "0.8rem", textAlign: "right" }}>
            Showing {rows.length} cheque(s)
            {selCount > 0 && <span style={{ marginLeft: 8, color: tab.color, fontWeight: 700 }}>· {selCount} selected</span>}
          </div>
        )}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      {/* Spinner CSS */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        button:hover { opacity: 0.88; }
      `}</style>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 24, right: 28, zIndex: 9999,
          background: toast.type === "error" ? "#fee2e2" : "#d1fae5",
          color: toast.type === "error" ? "#991b1b" : "#065f46",
          border: `1px solid ${toast.type === "error" ? "#fca5a5" : "#6ee7b7"}`,
          borderRadius: 12, padding: "12px 22px", fontWeight: 700,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", fontSize: "0.9rem",
          display: "flex", alignItems: "center", gap: 8,
          animation: "slideIn 0.3s ease",
        }}>
          {toast.type === "error" ? <FaTimesCircle /> : <FaCheckCircle />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={S.header}>
        <div style={S.iconBox}><FaMoneyCheckAlt /></div>
        <div>
          <h1 style={S.title}>Cheque Manager</h1>
          <p style={S.subtitle}>
            Manage Post-Dated Cheques (PDC) — receipts &amp; payments — with status tracking and reconciliation
          </p>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
          <div style={{
            background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10,
            padding: "8px 14px", fontSize: "0.8rem", color: "#166534",
            fontWeight: 700, display: "flex", alignItems: "center", gap: 6,
          }}>
            <FaArrowDown style={{ color: "#0077cc" }} />
            PDC Receipts
          </div>
          <div style={{
            background: "#faf5ff", border: "1px solid #ddd6fe", borderRadius: 10,
            padding: "8px 14px", fontSize: "0.8rem", color: "#6b21a8",
            fontWeight: 700, display: "flex", alignItems: "center", gap: 6,
          }}>
            <FaArrowUp style={{ color: "#7c3aed" }} />
            PDC Payments
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={S.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            id={`cheque-tab-${tab.key}`}
            style={S.tab(activeTab === tab.key, tab)}
            onClick={() => handleTabChange(tab.key)}
          >
            {tab.icon} {tab.label}
            {cheques[tab.key].length > 0 && (
              <span style={{
                background: activeTab === tab.key ? "rgba(255,255,255,0.3)" : "#e5e7eb",
                color: activeTab === tab.key ? "#fff" : "#6b7280",
                borderRadius: 20, padding: "1px 9px",
                fontSize: "0.72rem", fontWeight: 700, marginLeft: 4,
              }}>
                {cheques[tab.key].length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {TABS.map((tab) => activeTab === tab.key && (
        <div key={tab.key}>{renderTab(tab)}</div>
      ))}
    </div>
  );
}
